<template>
  <div class="duration-selector-container">
    <FormKit
        type="hidden"
        name="DateValidation"
        :validation="selectedDate ? 'finished' : 'required'"
    />
    <FormKit
        type="hidden"
        name="DurationValidation"
        :validation="selectedDuration ? 'finished' : 'required'"
    />
    <FormKit
        type="hidden"
        name="TimeValidation"
        :validation="selectedTimes.length === 0 ? 'required' : 'finished'"
    />
    <button v-for="duration in durations"
            :key="duration"
            @click="setDuration(duration)"
            :class="{'selected': selectedDuration === duration}"
            class="duration-button"
            type="button"
    >
      {{ duration }} min
    </button>
  </div>
  <div class="date-time-picker-container">
    <div class="date-picker-container">
      <DatePicker
          v-model="selectedDate"
          :six-weeks="true"
          :disabled-dates="disableSpecificDates"
          :enable-time-picker="false"
          :format="dateFormat"
          inline
          auto-apply
          class="full-width-height"
          locale="de"
          :disabled="!disabledDatesArray"
      />
    </div>
    <div style="display: block; width: 100%; position: relative;">
      <div v-if="timeLoading" class="loading-overlay">
        <img class="loading-spinner" src="../assets/logo.png" alt="">
      </div>
      <div v-if="!timeLoading" class="time-picker-container" :class="{ 'disabled-container': !disabledDatesArray }">

        <button v-for="(time, index) in times"
                :key="index"
                :disabled="!selectedDate || !time[1].available"
                type="button"
                @click="toggleTimeSelection(time)"
                :class="{
                    'time-picker-button': selectedDate && time[1].available,
                    'time-picker-button-fully-disabled': !selectedDate || !time[1].available,
                    'selected': selectedTimes.includes(time)
                  }"
        >
          <span v-if="time[1].break" class="material-symbols-outlined cafe-icon">☕</span>
          {{ time[0] }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import DatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import dateTimeService from "@/services/dateTimeService";
import configService from "@/services/configService";

export default {
  emits: ['updateDateTime'],

  components: {
    DatePicker,
  },
  props: {
    currentWorker: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      selectedDate: null,
      config: null,
      selectedTimes: [],
      times: [],
      selectedDuration: null,
      durations: [],
      dateFormat: 'dd/MM/yyyy',
      disabledDays: [],
      disabledDatesArray: [],
      appointments: [],
      timeLoading: true
    };
  },
  computed: {
    disableSpecificDates() {
      return (date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const futureMinDate = new Date(today);
        if (this.config && this.config.futureMin) {
          futureMinDate.setDate(today.getDate() + parseInt(this.config.futureMin, 10));
        }

        const futureLimitDate = new Date(today);
        if (this.config && this.config.futureLimit) {
          futureLimitDate.setDate(today.getDate() + parseInt(this.config.futureLimit, 10));
        }

        if (date < futureMinDate) return true;
        if (date > futureLimitDate) return true;
        if (this.disabledDays.includes(date.getDay())) return true;
        return this.disabledDatesArray.some(d => d.getTime() === date.getTime());
      };
    },
    selectedDateTime() {
      if (!this.selectedDate || this.selectedTimes.length === 0) return null;
      const [timeString] = this.selectedTimes[0];
      const [hours, minutes] = timeString.split(':').map(Number);
      const date = new Date(this.selectedDate);
      date.setHours(hours, minutes, 0, 0);
      return date;
    }
  },
  async mounted() {
    try {
      this.config = await configService.getConfigs();
      this.durations = this.config.durations;
      this.disabledDays = await this.getDisabledDays();
      this.appointments = await dateTimeService.fetchAppointments(this.currentWorker.email);
      await this.loadTimes();
    } catch (error) {
      console.error('Error during mounted lifecycle hook:', error);
    }
  },
  watch: {
    selectedDate() {
      this.loadTimes();
      this.emitSelection();
    },
    selectedDuration() {
      this.loadTimes();
      this.emitSelection();
    },
    selectedTimes() {
      this.emitSelection();
    },
  },
  methods: {
    async loadTimes() {
      this.selectedTimes = [];
      this.timeLoading = true;

      const config = this.config;
      const timeSpan = parseInt(config.timeSpan, 10);

      const workdayConfig = config.workday;
      const lunchBreakConfig = config.lunchBreak;

      const appointmentDate = new Date(this.selectedDate);
      const dayOfWeek = appointmentDate.toLocaleString('en-US', { weekday: 'long' });

      const workday = workdayConfig[dayOfWeek];
      const lunchBreak = lunchBreakConfig[dayOfWeek];

      if (!workday || workday.disabled === true || !lunchBreak || lunchBreak.disabled === true) {
        this.times = [];
        this.timeLoading = false;
        return;
      }

      const [workdayStart, workdayEnd] = [workday.startTime, workday.endTime].map(time => this.convertTimeToMinutes(time));
      const [lunchBreakStart, lunchBreakEnd] = [lunchBreak.startTime, lunchBreak.endTime].map(time => this.convertTimeToMinutes(time));

      const totalDayMinutes = workdayEnd - workdayStart;
      const timeSlots = Math.floor(totalDayMinutes / timeSpan);

      let times = [];

      for (let i = 0; i < timeSlots; i++) {
        const startMinutes = workdayStart + i * timeSpan;
        const endMinutes = startMinutes + (this.selectedDuration || timeSpan);

        if (endMinutes > workdayEnd) continue;

        let isValidTimeSlot = true;

        const isDuringLunchBreak = startMinutes < lunchBreakEnd && endMinutes > lunchBreakStart;

        isValidTimeSlot = !isDuringLunchBreak && !this.isTimeSlotBooked(startMinutes, endMinutes);

        if (new Date(this.selectedDate).setHours(0, 0, 0, 0) === new Date().setHours(0, 0, 0, 0)) {
          if (startMinutes < (new Date().getHours() * 60 + new Date().getMinutes())) {
            isValidTimeSlot = false;
          }
        }

        times.push([
          this.convertMinutesToTime(startMinutes),
          { available: isValidTimeSlot, break: isDuringLunchBreak }
        ]);
      }

      this.times = times;
      this.timeLoading = false;
    },

    isTimeSlotBooked(startMinutes, endMinutes) {
      const startDate = this.getDateTimeFromMinutes(startMinutes);
      const endDate = this.getDateTimeFromMinutes(endMinutes);

      return this.appointments.some(appointment =>
          (startDate < appointment.end && endDate > appointment.start)
      );
    },
    getDateTimeFromMinutes(minutes) {
      const date = new Date(this.selectedDate);
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      date.setHours(hours, mins, 0, 0);
      return date;
    },
    setDuration(duration) {
      this.selectedDuration = duration;
      this.selectedTimes = [];
      this.emitSelection();
    },
    convertTimeToMinutes(time) {
      const [hours, minutes] = time.split(':').map(Number);
      return hours * 60 + minutes;
    },
    convertMinutesToTime(minutes) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
    },
    toggleTimeSelection(time) {
      if (this.selectedTimes.includes(time)) {
        this.selectedTimes = this.selectedTimes.filter((t) => t !== time);
      } else {
        this.selectedTimes = [time];
      }
      this.emitSelection();
    },
    async getDisabledDays() {
      const disabledDays = this.config.disabledDays || [];
      const disabledDaysOfWeek = [];

      const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

      daysOfWeek.forEach((day, index) => {
        const workday = this.config.workday[day];
        if (workday && workday.disabled) {
          disabledDaysOfWeek.push(index);
        }
      });

      return [...new Set([...disabledDays, ...disabledDaysOfWeek])];
    },

    emitSelection() {
      const jsonData = {
        date: this.selectedDateTime,
        duration: this.selectedDuration
      };
      this.$emit('updateDateTime', jsonData);
    }
  },
};
</script>






<style scoped>
.date-time-picker-container {
  display: flex;
  flex-direction: row;
  gap: 20px;
  height: 328px;
}

.date-picker-container {
  height: 100%;
}

.duration-selector-container {
  display: flex;
  flex-direction: row;
  gap: 5px;
}

.duration-button {
  background: white;
  border: #2c3e50 solid 1px;
  border-radius: 4px;
  padding: 5px;
  text-align: center;
  cursor: pointer;
  transition: 0.3s;
  width: 100%;
  height: 100%;
  margin-bottom: 20px;
}

.duration-button:hover {
  background-color: rgba(32, 127, 200, 0.14);
}

.duration-button.selected {
  background-color: #006ff9;
  color: white;
}

.time-picker-container {
  display: flex;
  gap: 5px;
  width: 100%;
  flex-direction: column;
  height: 100%;
  overflow-y: scroll;
}

.time-picker-button {
  background: white;
  border: #2c3e50 solid 1px;
  border-radius: 4px;
  height: 100%;
  min-height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  transition: 0.3s;
  cursor: pointer;
}

.time-picker-button:hover {
  background-color: rgba(32, 127, 200, 0.14);
}

.time-picker-button:active {
  background-color: #567994;
}

.time-picker-button.selected {
  background-color: #006ff9;
  color: white;
}

.time-picker-button-fully-disabled {
  background: white;
  border: #2c3e50 solid 1px;
  border-radius: 4px;
  height: 100%;
  min-height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  transition: 0.3s;
  cursor: not-allowed;

}


.time-picker-container.disabled-container {
  cursor: not-allowed;
}



.loading-overlay {
  position: absolute;

  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99999999999 !important;
}

.loading-spinner {
  border-radius: 4px;
  width: 35px;
  height: 35px;
  animation: spin 2s linear infinite;

}

/*
border-radius: 4px;
width: 35px;
height: 35px;
animation: spin 2s linear infinite;
*/

@keyframes spin {
  0% {
    transform: rotate(0deg) scale(1);
  }
  50% {
    transform: rotate(180deg) scale(1.1);
  }
  100% {
    transform: rotate(360deg) scale(1);
  }
}

/* @keyframes spin {
0% { transform: rotate(0deg); }
100% { transform: rotate(360deg); }
 } */

.cafe-icon {
  position: absolute;
  left: 7px;
  font-size: 20px !important;
}

@media (max-width: 600px) {
  .date-time-picker-container {
    display: flex;
    flex-direction: column;
    height: auto;
  }




  .date-picker-container {
    position: relative;
    width: 100%;
    display: inline-block;
    min-width: max-content;
    height: 100%;
    align-self: center;
    transform: scale(90%);

  }

  .loading-overlay{
    min-height: 70px;
    position: relative;
  }



  .time-picker-container {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-auto-rows: auto;
    gap: 10px;
    max-width: 100%;
  }
}

</style>
